import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '@/store';
import { logger } from '@/utils/logger';
import type { Player, WSMessage } from '@party-game/shared';

// Message handler type
type MessageHandler = (message: WSMessage) => void;

/**
 * مدير WebSocket - Singleton للحفاظ على اتصال واحد
 * يستخدم نظام listeners قائم على نوع الرسالة
 */
class WebSocketManager {
  private ws: WebSocket | null = null;
  // Type-specific listeners - يتم استدعائها أولاً
  private typeListeners = new Map<string, Set<MessageHandler>>();
  // Global listeners - يتم استدعائها لكل رسالة
  private globalListeners = new Set<MessageHandler>();
  private statusListeners = new Set<(connected: boolean) => void>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private roomCode: string | null = null;
  private playerId: string | null = null;
  private playerName: string | null = null;
  private isReconnecting = false;

  connect(roomCode: string, playerId: string, playerName: string): void {
    // إذا كان متصل أو في مرحلة الاتصال بنفس الغرفة، لا تعيد الاتصال
    if (this.ws && this.roomCode === roomCode) {
      const state = this.ws.readyState;
      if (state === WebSocket.OPEN) {
        logger.info('WS', 'Already connected to room', { roomCode });
        // Notify new listeners that we're already connected
        this.notifyStatus(true);
        return;
      }
      if (state === WebSocket.CONNECTING) {
        logger.info('WS', 'Already connecting to room', { roomCode });
        return;
      }
    }

    // إذا هناك اتصال قائم لغرفة أخرى، أغلقه
    if (this.ws && this.roomCode !== roomCode) {
      this.ws.close(1000, 'Switching rooms');
      this.ws = null;
    }

    this.roomCode = roomCode;
    this.playerId = playerId;
    this.playerName = playerName;

    // Determine API URL based on environment
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    let wsUrl: string;
    
    if (isDev) {
      // Local development
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.hostname}:8787/api/rooms/${roomCode}/ws?playerId=${playerId}&playerName=${encodeURIComponent(playerName)}`;
    } else {
      // Production - use Cloudflare Worker URL
      const apiUrl = import.meta.env.VITE_WS_URL || 'wss://party-game-api.ibrahem-magdy025.workers.dev';
      wsUrl = `${apiUrl}/api/rooms/${roomCode}/ws?playerId=${playerId}&playerName=${encodeURIComponent(playerName)}`;
    }

    logger.info('WS', 'Connecting', { roomCode, wsUrl: wsUrl.split('?')[0] });

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      logger.info('WS', 'Connected');
      this.isReconnecting = false;
      this.notifyStatus(true);
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WSMessage;
        const type = message.type;
        
        // Get listener counts for logging
        const typeCount = this.typeListeners.get(type)?.size || 0;
        const globalCount = this.globalListeners.size;
        
        logger.info('WS', `📩 [${type}]`, { 
          typeListeners: typeCount,
          globalListeners: globalCount,
        });
        
        // 1️⃣ Call type-specific listeners FIRST (synchronously)
        const specificListeners = this.typeListeners.get(type);
        if (specificListeners && specificListeners.size > 0) {
          specificListeners.forEach(fn => {
            try {
              fn(message);
            } catch (e) {
              logger.error('WS', `Error in ${type} listener`, e);
            }
          });
        }
        
        // 2️⃣ Then call global listeners
        this.globalListeners.forEach(fn => {
          try {
            fn(message);
          } catch (e) {
            logger.error('WS', 'Error in global listener', e);
          }
        });
      } catch (e) {
        logger.error('WS', 'Parse error', e);
      }
    };

    this.ws.onclose = (event) => {
      logger.info('WS', 'Closed', { code: event.code });
      this.notifyStatus(false);
      
      // لا تعيد الاتصال إذا كان إغلاق عادي
      if (event.code === 1000) {
        this.ws = null;
        return;
      }

      // إعادة الاتصال
      if (!this.isReconnecting && this.roomCode) {
        this.isReconnecting = true;
        this.reconnectTimer = setTimeout(() => {
          if (this.roomCode && this.playerId && this.playerName) {
            this.connect(this.roomCode, this.playerId, this.playerName);
          }
        }, 2000);
      }
    };

    this.ws.onerror = (error) => {
      logger.error('WS', 'Error', error);
    };
  }

  send(message: Omit<WSMessage, 'timestamp'>): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ ...message, timestamp: new Date().toISOString() }));
      return true;
    }
    return false;
  }

  close(reason = 'User disconnect'): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, reason);
      this.ws = null;
    }
    this.roomCode = null;
    this.playerId = null;
    this.isReconnecting = false;
  }

  /**
   * Register listener for specific message type
   * These are called BEFORE global listeners
   */
  on(type: string, fn: MessageHandler): () => void {
    if (!this.typeListeners.has(type)) {
      this.typeListeners.set(type, new Set());
    }
    this.typeListeners.get(type)!.add(fn);
    logger.info('WS', `📌 Registered [${type}] listener`, { 
      count: this.typeListeners.get(type)!.size 
    });
    return () => {
      this.typeListeners.get(type)?.delete(fn);
      logger.info('WS', `📌 Unregistered [${type}] listener`);
    };
  }

  /**
   * Register global listener for all messages
   * Called after type-specific listeners
   */
  onAny(fn: MessageHandler): () => void {
    this.globalListeners.add(fn);
    return () => this.globalListeners.delete(fn);
  }

  onStatusChange(fn: (connected: boolean) => void): () => void {
    this.statusListeners.add(fn);
    // أرسل الحالة الحالية فوراً
    const currentStatus = this.ws?.readyState === WebSocket.OPEN;
    logger.info('WS', 'Status listener registered', { 
      currentStatus, 
      readyState: this.ws?.readyState,
      listenersCount: this.statusListeners.size 
    });
    fn(currentStatus);
    return () => this.statusListeners.delete(fn);
  }

  private notifyStatus(connected: boolean): void {
    logger.info('WS', 'Notifying status change', { 
      connected, 
      listenersCount: this.statusListeners.size 
    });
    this.statusListeners.forEach(fn => fn(connected));
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get currentRoom(): string | null {
    return this.roomCode;
  }
}

// Singleton instance
export const wsManager = new WebSocketManager();

/**
 * React Hook للتعامل مع WebSocket
 * يوفر طريقة بسيطة للاتصال واستقبال الرسائل
 */
export function useWebSocket(roomCode: string, playerId: string, playerName: string) {
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(wsManager.isConnected);

  const { setPlayers, setRoom, setIsHost } = useGameStore();

  // الاتصال وتسجيل handlers للرسائل العامة
  useEffect(() => {
    if (!roomCode || !playerId || !playerName) return;

    // Register status listener FIRST before connecting
    const unsubStatus = wsManager.onStatusChange(setIsConnected);
    
    // Now connect (will notify via status listener when connected)
    wsManager.connect(roomCode, playerId, playerName);

    // Handle common room messages
    const unsubRoomUpdate = wsManager.on('room:update', (msg) => {
      const { room, players } = msg.payload as { room: any; players: Player[] };
      if (room) setRoom(room);
      if (players) {
        setPlayers(players);
        const me = players.find(p => p.id === playerId);
        if (me) setIsHost(me.role === 'host');
      }
    });

    const unsubPlayerJoined = wsManager.on('room:player-joined', (msg) => {
      const { players } = msg.payload as { players?: Player[] };
      if (players) {
        setPlayers(players);
        const me = players.find(p => p.id === playerId);
        if (me) setIsHost(me.role === 'host');
      }
    });

    const unsubPlayerLeft = wsManager.on('room:player-left', (msg) => {
      const { players } = msg.payload as { players?: Player[] };
      if (players) setPlayers(players);
    });

    const unsubGameStart = wsManager.on('game:start', (msg) => {
      const { room } = msg.payload as { room?: any };
      if (room) setRoom(room);
      navigate(`/game/${roomCode}`);
    });

    const unsubGameEnd = wsManager.on('game:end', () => {
      navigate(`/results/${roomCode}`);
    });

    const unsubScoreUpdate = wsManager.on('score:update', (msg) => {
      const { players } = msg.payload as { players: Player[] };
      if (players) setPlayers(players);
    });

    return () => {
      unsubStatus();
      unsubRoomUpdate();
      unsubPlayerJoined();
      unsubPlayerLeft();
      unsubGameStart();
      unsubGameEnd();
      unsubScoreUpdate();
    };
  }, [roomCode, playerId, playerName, navigate, setRoom, setPlayers, setIsHost]);

  const sendMessage = useCallback((message: Omit<WSMessage, 'timestamp'>) => {
    wsManager.send(message);
  }, []);

  const disconnect = useCallback(() => {
    wsManager.close();
  }, []);

  /**
   * Register callback for specific message type
   * This is the RECOMMENDED way to handle messages in components
   * Returns cleanup function
   */
  const on = useCallback((type: string, callback: MessageHandler) => {
    return wsManager.on(type, callback);
  }, []);

  return {
    isConnected,
    sendMessage,
    on,
    disconnect,
    reconnect: () => wsManager.connect(roomCode, playerId, playerName),
  };
}
